import {
  CUSTOMER_AGREEMENT_FOLDER,
  getInvestmentAdvisoryAgreementConfig,
} from "../../components/profile/InvestmentAdvisoryAgreementModal";
import type { Agreement } from "../../services/agreementsService";
import blobService from "../../services/blobService";

export interface SignedDocument {
  id: number;
  agreementName: string;
  title: string;
  signedName: string | null;
  signedAtUtc: string;
  folder: string;
  filename: string;
  viewable: boolean;
}

interface SignedDocumentKind {
  title: string;
  fallbackFilename: string;
}

const STANDARD_AGREEMENT = getInvestmentAdvisoryAgreementConfig(false);
const RETIREMENT_AGREEMENT = getInvestmentAdvisoryAgreementConfig(true);

// Only the two investment advisory agreements are customer-facing documents.
// Every other row saved under /v2/agreements (for example
// investor-certification) is an internal acknowledgement and is excluded.
const SIGNED_DOCUMENT_KINDS = new Map<string, SignedDocumentKind>([
  [STANDARD_AGREEMENT.name, {
    title: "Investment Advisory Agreement",
    fallbackFilename: STANDARD_AGREEMENT.filename,
  }],
  [RETIREMENT_AGREEMENT.name, {
    title: "Investment Advisory Agreement (Retirement Accounts)",
    fallbackFilename: RETIREMENT_AGREEMENT.filename,
  }],
]);

const toSortableTime = (iso: string): number => {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? 0 : time;
};

/**
 * Maps the signed-agreement rows of the authenticated user to the documents
 * the profile page lists. Newest signature first.
 */
export function toSignedDocuments(agreements: Agreement[]): SignedDocument[] {
  return agreements
    .flatMap((agreement): SignedDocument[] => {
      const kind = SIGNED_DOCUMENT_KINDS.get(agreement.agreementName);
      if (!kind) return [];

      // agreementDocument is stored as "Customer Agreement/<filename>". Rows
      // saved without it fall back to the current configured document.
      const documentPath = agreement.agreementDocument?.trim() ?? "";
      const folder = documentPath
        ? blobService.extractFileFolder(documentPath)
        : CUSTOMER_AGREEMENT_FOLDER;
      const filename = documentPath
        ? blobService.extractFileName(documentPath)
        : kind.fallbackFilename;
      const signedName = agreement.data?.signedName;
      const signedAt = agreement.data?.signedAt;

      return [{
        id: agreement.id,
        agreementName: agreement.agreementName,
        title: kind.title,
        signedName: typeof signedName === "string" ? signedName : null,
        signedAtUtc: typeof signedAt === "string" ? signedAt : agreement.updatedAtUtc,
        folder,
        filename,
        viewable: folder === CUSTOMER_AGREEMENT_FOLDER && filename !== "",
      }];
    })
    .sort((a, b) => toSortableTime(b.signedAtUtc) - toSortableTime(a.signedAtUtc));
}
